import { CameraService } from '../core/CameraService';
import { Core } from '../core/Core';
import { TickService } from '../core/TickService';
import { CharacterAnimatorService } from './CharacterAnimatorService';
import { ErrorStrings, IncomingFlutterMessage, IncomingFlutterMessageType, OutgoingAsynchronousMessage, OutgoingAsynchronousMessageType, OutgoingSynchronousMessage } from '../data/BridgeData';
import { EquipmentIds } from '../data/EquipmentData';
import { CameraAngleName } from '../data/CameraAngles';

export class FlutterBridgeService {
	/**
	 * Properties
	 * */
	core: Core;

	/**
	 * Constructor
	 * */
	constructor(core: Core) {
		this.core = core;
		(window as any).flutterMessage = this.receiveFlutterMessage.bind(this);

		return this;
	}

	/**
	 * Receives a message from Flutter
	 */
	receiveFlutterMessage(message: IncomingFlutterMessage): OutgoingSynchronousMessage {
		const sCharacterAnimator: CharacterAnimatorService = this.core.s('CharacterAnimator');

		try {
			let synchronousResponse: OutgoingSynchronousMessage;

			if (typeof (message) !== "object") {
				message = JSON.parse(message);
			}

			// Register services we may need
			const sCamera: CameraService = this.core.s('Camera');

			// Act based on the messaage
			switch (message.event_type) {

				/////////////////////////////////////////
				////////////// ASYNCHRONOUS COMMANDS
				case IncomingFlutterMessageType.register_animation:
					const registerResult = sCharacterAnimator.registerAnimation({
						id: message.payload.exercise_variant_id,
						json: message.payload.animation_json,
						playbackSpeed: message.payload.playback_speed,
						autoBlend: true
					});

					if (registerResult.wasSuccessful) {
						this.sendAsynchronousMessage({ type: OutgoingAsynchronousMessageType.animation_registered, payload: { exercise_variant_id: message.payload.exercise_variant_id, result: 'ok' } });
						synchronousResponse = { result: "ok" };
					} else {
						this.sendAsynchronousMessage({ type: OutgoingAsynchronousMessageType.animation_registered, payload: { exercise_variant_id: message.payload.exercise_variant_id, result: 'error', error: registerResult.error } });
						synchronousResponse = { result: "error", error: registerResult.error };
					}
					break;

				case IncomingFlutterMessageType.dispose_animation:
					const deregisterResult = sCharacterAnimator.deregisterAnimation(message.payload.exercise_variant_id);

					if (deregisterResult.wasSuccessful) {
						this.sendAsynchronousMessage({ type: OutgoingAsynchronousMessageType.animation_disposed, payload: { exercise_variant_id: message.payload.exercise_variant_id, result: 'ok' } });
						synchronousResponse = { result: "ok" };
					} else {
						this.sendAsynchronousMessage({ type: OutgoingAsynchronousMessageType.animation_disposed, payload: { exercise_variant_id: message.payload.exercise_variant_id, result: 'error', error: deregisterResult.error } });
						synchronousResponse = { result: "error", error: deregisterResult.error };
					}
					break;

				/////////////////////////////////////////
				////////////// SYNCHRONOUS COMMANDS
				case IncomingFlutterMessageType.set_camera:
					const cameraResult = sCamera.switchCameraAngle(message.payload.camera_angle);

					if (cameraResult.wasSuccessful) {
						synchronousResponse = { result: "ok" };
					} else {
						synchronousResponse = { result: "error", error: cameraResult.error };
					}
					break;

				case IncomingFlutterMessageType.set_equipment:
					const equipmentResult = this.core.exerciseScene.setVisibleEquipment(message.payload.equipment_ids);

					if (equipmentResult.wasSuccessful) {
						synchronousResponse = { result: "ok" };
					} else {
						synchronousResponse = { result: "error", error: equipmentResult.error };
					}
					break;

				case IncomingFlutterMessageType.character_facing:
					const facingResult = this.core.s('Character').setFacing(message.payload.is_mirrored);

					if (facingResult.wasSuccessful) {
						synchronousResponse = { result: "ok" };
					} else {
						synchronousResponse = { result: "error", error: facingResult.error };
					}
					break;

				case IncomingFlutterMessageType.character_eyes:
					const eyesResult = this.core.s('Character').setEyes(message.payload.are_closed);

					if (eyesResult.wasSuccessful) {
						synchronousResponse = { result: "ok" };
					} else {
						synchronousResponse = { result: "error", error: eyesResult.error };
					}
					break;

				case IncomingFlutterMessageType.character_fade:
					const fadeResult = message.payload.fade_in ? this.core.s('Character').fadeInCharacter() : this.core.s('Character').fadeOutCharacter();

					if (fadeResult.wasSuccessful) {
						synchronousResponse = { result: "ok" };
					} else {
						synchronousResponse = { result: "error", error: fadeResult.error };
					}
					break;

				case IncomingFlutterMessageType.play_animation:
					const playingResult = sCharacterAnimator.activateAnimation({
						id: message.payload.exercise_variant_id,
						weight: 1,
						fadeTimeMS: 750,
					});

					if (playingResult.wasSuccessful) {
						sCharacterAnimator.deactivateAnimation({
							id: "Idle",
							fadeTimeMS: 750,
						});

						synchronousResponse = { result: "ok" };
					} else {
						synchronousResponse = { result: "error", error: playingResult.error };
					}

					break;

				case IncomingFlutterMessageType.pause_tick:
					const sTick: TickService = this.core.s('Tick');
					sTick.setTickPause(message.payload.value);

					const darkener: HTMLElement | null = document.querySelector("#darkener");
					if (darkener) darkener.style.display = message.payload.value && "block" || "none";

					synchronousResponse = { result: "ok" };
					break;

				case IncomingFlutterMessageType.soft_idle:
					const softIdleResult = sCharacterAnimator.idle();

					if (softIdleResult.wasSuccessful) {
						synchronousResponse = { result: "ok" };
					} else {
						synchronousResponse = { result: "error", error: softIdleResult.error };
					}

					break;

				case IncomingFlutterMessageType.idle:
					const idleResult = sCharacterAnimator.idle();
					this.core.exerciseScene.setVisibleEquipment([EquipmentIds.mat]);
					sCamera.switchCameraAngle(CameraAngleName.front);

					if (idleResult.wasSuccessful) {
						synchronousResponse = { result: "ok" };
					} else {
						synchronousResponse = { result: "error", error: idleResult.error };
					}

					break;

				default:
					synchronousResponse = { result: "error", error: ErrorStrings.unknown_error };
			}

			if (!synchronousResponse) {
				this.sendAsynchronousMessage({ type: OutgoingAsynchronousMessageType.unknown_error, payload: { exercise_variant_id: '', result: 'error', error: ErrorStrings.no_message_handler } });
				return { result: "error", error: ErrorStrings.no_message_handler };
			} else {
				return synchronousResponse;
			}

		} catch (e: any) {
			console.log(">>> JS UNHANDLED ERROR", e.type, e.message);
			this.sendAsynchronousMessage({ type: OutgoingAsynchronousMessageType.unknown_error, payload: { exercise_variant_id: '', result: 'error', error: e.message || ErrorStrings.no_message_handler } });
			return { result: "error", error: ErrorStrings.flutter_message_error };
		}
	}

	/**
	 * Sends a message to Flutter
	 */
	sendAsynchronousMessage(message: OutgoingAsynchronousMessage) {
		window.PlayerController?.postMessage(JSON.stringify({ event_type: message.type, payload: message.payload }));
	}
}